import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";

import { useMedplum, useMedplumProfile } from "@medplum/react";

import { Button, Flex, Table } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { IconEdit, IconListCheck, IconTrash } from "@tabler/icons-react";
import { Fragment } from "react/jsx-runtime";

import ConfirmationModal from "@/components/ConfirmationModal";
import EmptyRow from "@/components/EmptyRow";
import ScrollableTable from "@/components/ScrollableTable";
import SortableTh from "@/components/SortableTh";
import { TextWrapper } from "@/components/TextWrapper";
import { fetchChecklistTemplates } from "@/services/checklist";
import { TABLE_MIN_WIDTH } from "@/settings/constants/teams";
import { AppDispatch, useAppDispatch } from "@/store";
import {
  getSelectedTemplate,
  loadTemplates,
  removeTemplate,
  selectTemplatesList,
  setSelectedTemplate,
} from "@/store/slices/checklist-templates";
import { parsePlanDefinitionToChecklistTemplateTableData } from "@/utils/checklist-template";
import { showSystemErrorNotification, toast } from "@/utils/notifications";

import { ChecklistTemplateTableDataType } from "../../../../types/checklist-template";
import {
  CHECKLIST_TEMPLATE_SHARE_SETTINGS_LABELS,
  CHECKLIST_TEMPLATE_SORT_KEYS,
  ChecklistTemplateShareSettingsOptions,
} from "../constants/checklist-template";
import { softDeleteChecklistTemplate } from "../services/template";
import { getChecklistTemplateValidSortKey } from "../utils/template";

function ChecklistTemplateTable({ onOpenTemplateDrawer }: { onOpenTemplateDrawer: () => void }) {
  const medplum = useMedplum();
  const dispatch = useAppDispatch();
  const profile = useMedplumProfile();
  const templates = useSelector(selectTemplatesList);
  const templatesTableData = parsePlanDefinitionToChecklistTemplateTableData(templates);
  const selectedTemplate = useSelector(getSelectedTemplate);
  const { practiceId } = useParams<{ practiceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const sortKey = getChecklistTemplateValidSortKey(
    searchParams.get("sort") as CHECKLIST_TEMPLATE_SORT_KEYS | undefined,
  );
  const [isDeleting, { open: startDeleting, close: stopDeleting }] = useDisclosure(false);
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);

  useEffect(() => {
    (async () => {
      try {
        const templatesResults = await fetchChecklistTemplates(
          medplum,
          practiceId,
          profile?.id,
          sortKey,
        );
        dispatch(loadTemplates(templatesResults));
      } catch {
        toast({
          type: "error",
          title: "Failed to load checklist templates",
          message: "Checklist templates could not be loaded. Refresh the page to retry.",
          autoClose: false,
        });
      }
    })();
  }, [medplum, practiceId, profile?.id, dispatch, sortKey, searchParams]);

  const onDelete = async () => {
    try {
      startDeleting();
      if (!selectedTemplate?.id) return;
      await softDeleteChecklistTemplate(medplum, selectedTemplate);
      dispatch(removeTemplate(selectedTemplate.id));
    } catch (error) {
      showSystemErrorNotification();
      throw error;
    } finally {
      dispatch(setSelectedTemplate(null));
      closeDeleteModal();
      stopDeleting();
    }
  };

  return (
    <Fragment>
      <ConfirmationModal
        onConfirm={onDelete}
        isDeleting={isDeleting}
        cancelButtonText="Cancel"
        opened={isDeleteModalOpen}
        confirmButtonText="Yes, Delete Template"
        title="Are you sure you want to delete this template?"
        description="Deleting this template is permanent and cannot be undone."
        onClose={() => {
          dispatch(setSelectedTemplate(null));
          closeDeleteModal();
        }}
      />
      <ScrollableTable miw={TABLE_MIN_WIDTH}>
        <TableHeader searchParams={searchParams} setSearchParams={setSearchParams} />
        <TableBody
          dispatch={dispatch}
          openDeleteModal={openDeleteModal}
          templatesTableData={templatesTableData}
          onOpenTemplateDrawer={onOpenTemplateDrawer}
        />
      </ScrollableTable>
    </Fragment>
  );
}

function TableHeader({
  setSearchParams,
  searchParams,
}: {
  setSearchParams: React.Dispatch<React.SetStateAction<URLSearchParams>>;
  searchParams: URLSearchParams;
}) {
  const handleSort = (sortKey: string | null) => {
    setSearchParams((prev) => {
      if (sortKey) {
        prev.set("sort", sortKey);
      } else {
        prev.delete("sort");
      }
      return prev;
    });
  };

  return (
    <Table.Thead bg="cream.3">
      <Table.Tr>
        <SortableTh
          colSpan={4}
          onSort={handleSort}
          menuPosition="bottom-start"
          searchParams={searchParams}
          sortKey={CHECKLIST_TEMPLATE_SORT_KEYS.TEMPLATE_NAME}
        >
          Template Name
        </SortableTh>
        <Table.Th colSpan={2}>#Tasks</Table.Th>
        <Table.Th colSpan={1}>Created By</Table.Th>
        <Table.Th colSpan={1}>Share Settings</Table.Th>
        <Table.Th ta="right" colSpan={4}>
          Actions
        </Table.Th>
      </Table.Tr>
    </Table.Thead>
  );
}

function TableBody({
  templatesTableData,
  onOpenTemplateDrawer,
  openDeleteModal,
  dispatch,
}: {
  templatesTableData: ChecklistTemplateTableDataType[];
  onOpenTemplateDrawer: () => void;
  openDeleteModal: () => void;
  dispatch: AppDispatch;
}) {
  return (
    <Table.Tbody>
      {templatesTableData.map((template) => (
        <ChecklistTemplateRow
          key={template.id}
          template={template}
          dispatch={dispatch}
          openDeleteModal={openDeleteModal}
          onOpenTemplateDrawer={onOpenTemplateDrawer}
        />
      ))}

      {!templatesTableData.length && (
        <EmptyRow
          Icon={IconListCheck}
          onAdd={onOpenTemplateDrawer}
          addButtonLabel="Add a Template Now"
          text="No templates have been added."
        />
      )}
    </Table.Tbody>
  );
}

const ChecklistTemplateRow = ({
  template,
  openDeleteModal,
  onOpenTemplateDrawer,
  dispatch,
}: {
  template: ChecklistTemplateTableDataType;
  openDeleteModal: () => void;
  onOpenTemplateDrawer: () => void;
  dispatch: AppDispatch;
}) => {
  const onEdit = () => {
    dispatch(setSelectedTemplate(template.id));
    onOpenTemplateDrawer();
  };

  return (
    <Table.Tr>
      <Table.Td colSpan={4}>{template.templateName}</Table.Td>
      <Table.Td colSpan={2}>{template.tasks}</Table.Td>
      <Table.Td colSpan={1}>
        <TextWrapper fw={600}>{template.createdBy}</TextWrapper>
      </Table.Td>
      <Table.Td colSpan={1}>
        {
          CHECKLIST_TEMPLATE_SHARE_SETTINGS_LABELS[
            template.shareSettings as ChecklistTemplateShareSettingsOptions
          ]
        }
      </Table.Td>
      <Table.Td colSpan={4}>
        <Flex gap="sm" justify="flex-end">
          <Button
            p="0"
            color="red.7"
            variant="transparent"
            leftSection={<IconTrash size={24} />}
            onClick={() => {
              openDeleteModal();
              dispatch(setSelectedTemplate(template.id));
            }}
          >
            <TextWrapper variant="bold">Delete</TextWrapper>
          </Button>
          <Button
            p="0"
            onClick={onEdit}
            color="burgundy.9"
            variant="transparent"
            leftSection={<IconEdit size={24} />}
          >
            <TextWrapper variant="bold">Edit</TextWrapper>
          </Button>
        </Flex>
      </Table.Td>
    </Table.Tr>
  );
};

export default ChecklistTemplateTable;
